import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Stack,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Business,
  Lock,
  Save,
  Cancel,
  Close,
} from "@mui/icons-material";
import { useState, useEffect } from "react";

export default function ContactModal({ open, onClose, onSave, initialData, clients = [] }) {
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", password: "", client: "" });
  const [errors, setErrors] = useState({ password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        nom: initialData.nom || "",
        prenom: initialData.prenom || "",
        email: initialData.email || "",
        telephone: initialData.telephone || "",
        password: "",
        client: initialData.client || "",
      });
      setErrors({ password: "" });
    } else {
      setForm({ nom: "", prenom: "", email: "", telephone: "", password: "", client: "" });
      setErrors({ password: "" });
    }
    // Réinitialiser saving quand le modal s'ouvre ou se ferme
    setSaving(false);
  }, [initialData, open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (e.target.name === "password") {
      setErrors({ ...errors, password: "" });
    }
  };

  const handleSubmit = () => {
    if (!initialData && !form.password) {
      setErrors({ ...errors, password: "Le mot de passe est requis pour la création." });
      return;
    }
    
    if (!form.nom || !form.prenom || !form.email || !form.client) {
      return;
    }

    setSaving(true);

    onSave(form);
    

    setTimeout(() => {
      setSaving(false);
    }, 2000);
  };

  const getInitials = () => {
    const first = form.prenom?.charAt(0)?.toUpperCase() || "";
    const last = form.nom?.charAt(0)?.toUpperCase() || "";
    return first + last || "?";
  };

  const isFormValid = form.nom && form.prenom && form.email && form.client && (initialData || form.password);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="600">
                {initialData ? "Modifier" : "Ajouter"} un contact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {initialData ? "Modifiez les informations du contact" : "Créez un nouveau contact"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prénom"
                name="prenom"
                value={form.prenom}
                onChange={handleChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom"
                name="nom"
                value={form.nom}
                onChange={handleChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Téléphone"
                name="telephone"
                value={form.telephone}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ position: 'relative' }}>
                <Business 
                  sx={{ 
                    position: 'absolute', 
                    left: 14, 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    color: 'action.active',
                    pointerEvents: 'none',
                  }} 
                  fontSize="small"
                />
                <FormControl fullWidth required>
                  <InputLabel id="client-label" sx={{ pl: 4 }}>Client</InputLabel>
                  <Select
                    labelId="client-label"
                    label="Client"
                    name="client"
                    value={form.client}
                    onChange={handleChange}
                    sx={{
                      pl: 4,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.nom || c.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mot de passe"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                required={!initialData}
                error={Boolean(errors.password)}
                helperText={errors.password || (initialData ? "Laisser vide pour ne pas changer" : "Requis pour la création")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color={errors.password ? "error" : "action"} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: errors.password ? 'error.main' : 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          {form.prenom && form.nom && (
            <>
              <Divider />
              <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  {getInitials()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="600">
                    Aperçu
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.prenom} {form.nom}
                  </Typography>
                </Box>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={saving}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          disabled={!isFormValid || saving}
          sx={{
            minWidth: 140,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {saving ? (initialData ? "Modification..." : "Ajout...") : (initialData ? "Modifier" : "Ajouter")}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 